import React from 'react';
import { inject, observer } from 'mobx-react';
import type { AppStore } from '@stores/app/store';
import type { SiteStore } from '@stores/site/store';
import type { UserAction } from '@stores/user/action';
import type { ThreadAction } from '@stores/thread/action';
import H5 from './layout/h5';
import { BasePage } from '@components/Base/BasePage';
import styles from './index.module.scss';
import type { BasePageProps } from '../../types/base';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router-dom';
import type { FeedbackListRequestParams } from '@components/feedbackFilter';

interface FeedbackListSearchProps extends BasePageProps, RouteComponentProps {
    app?: AppStore;
    site?: SiteStore;
    user?: UserAction;
    thread?: ThreadAction;
}

interface FeedbackListSearchState {
    loading: boolean;
    error: any;
    pageLoaded: any;
    searchInputValue: string;
    searchKeyword: string;
    /** 是否已执行过搜索，未搜索时不请求列表、不展示空态 */
    hasSearched: boolean;
}

/** H5 问题反馈搜索页：独立路由，隐藏 header 仅保留下载 App 入口 */
@inject('app', 'thread')
@observer
class FeedbackListSearch extends BasePage<FeedbackListSearchProps, FeedbackListSearchState> {
    namespace = 'feedbackListSearch';

    state: FeedbackListSearchState = {
        loading: false,
        error: null,
        pageLoaded: false,
        searchInputValue: '',
        searchKeyword: '',
        hasSearched: false,
    };

    componentDidMount() {
        const { app, history, thread } = this.props;
        if (app?.platform === 'pc') {
            history.replace('/feedbackList');
            return;
        }
        thread?.listStore.clearList({ namespace: this.namespace });
        super.componentDidMount();
    }

    buildFeedbackListParams = (page = 1): FeedbackListRequestParams => {
        const { searchKeyword } = this.state;
        const params: FeedbackListRequestParams = {
            'page[number]': page,
            'page[size]': 20,
        };

        const keyword = searchKeyword.trim();
        if (keyword) {
            params['filter[keywords]'] = keyword;
        }

        return params;
    };

    getFeedbackList = () => {
        const { thread } = this.props;
        if (!thread) return;

        thread.listStore.clearList({ namespace: this.namespace });
        window.scrollTo(0, 0);
        thread.getFeedbackList(this.buildFeedbackListParams(1), this.namespace);
    };

    handleSearchChange = (value: string) => {
        this.setState({ searchInputValue: value });
    };

    handleSearchConfirm = () => {
        const keyword = this.state.searchInputValue.trim();
        if (!keyword) {
            return;
        }
        this.setState({
            hasSearched: true,
            searchKeyword: keyword,
        }, () => {
            this.getFeedbackList();
        });
    };

    handleLoadMore = () => {
        if (!this.state.hasSearched) {
            return;
        }
        const { thread } = this.props;
        if (!thread) return;

        const list = thread.listStore.lists[this.namespace];
        if (!list || !list.hasMore || list.loading) return;

        const nextPage = (list.attribs?.page || 1) + 1;
        thread.getFeedbackList(this.buildFeedbackListParams(nextPage), this.namespace);
    };

    render() {
        const { app, thread } = this.props;
        const platform = app?.platform || 'h5';

        if (platform === 'pc') {
            return null;
        }

        const listStore = thread?.listStore;
        const list = listStore?.lists[this.namespace];
        const feedbackList = listStore?.getList({ namespace: this.namespace }) || [];
        const { searchInputValue, searchKeyword, hasSearched } = this.state;

        if (!app?.platform) {
            return (
                <div className={styles.loading} style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                }}>
                    <div>加载中...</div>
                </div>
            );
        }

        return (
            <div className={styles.h5Wrap}>
                <H5
                    feedbackList={feedbackList}
                    loading={list?.loading || false}
                    hasMore={list?.hasMore ?? true}
                    hasSearched={hasSearched}
                    onLoadMore={this.handleLoadMore}
                    searchInputValue={searchInputValue}
                    searchKeyword={searchKeyword}
                    onSearchChange={this.handleSearchChange}
                    onSearchConfirm={this.handleSearchConfirm}
                />
            </div>
        );
    }
}

export default withRouter(FeedbackListSearch);
